var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useState, useMemo } from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, InputAdornment, TextField, Typography, Autocomplete, Grid, Chip, Divider, } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { CaretLeft, CaretRight, MagnifyingGlass, PencilSimple, Plus, Trash, Warning, } from "@phosphor-icons/react";
import AdditionalLeaderboardApi from "../../../infrastructure/api/pages/additional-leaderboard-api";
import { convertToNLEuroFormat } from "../../../application/common/utils/helper";
import { DATA_GRID_SX } from "../../components/dashboard/kpi-shared-styles";
import CustomModal from "../modal/CustomModal";
const EMPTY_FORM = {
    salesAgentId: "",
    salesAgentName: "",
    entryDate: new Date().toISOString().slice(0, 10),
    tiUitbreidingInOfferte: "",
    tiUitbreidingGewonnen: "",
    tiUitbreidingOmzet: "",
    tiMeerwerkOffertesGewonnen: "",
    tiMeerwerkInkomsten: "",
    ppeOffertesGewonnen: "",
    ppeInkomsten: "",
};
export const AdditionalLeaderboardFormModal = ({ open, onClose, canCreate, canManage, canDelete, }) => {
    const currentYear = new Date().getFullYear();
    const [year, setYear] = useState(currentYear);
    const [entries, setEntries] = useState([]);
    const [agents, setAgents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [formOpen, setFormOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formSaving, setFormSaving] = useState(false);
    const [formData, setFormData] = useState(Object.assign({}, EMPTY_FORM));
    const [selectedAgent, setSelectedAgent] = useState(null);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const fetchEntries = (y) => __awaiter(void 0, void 0, void 0, function* () {
        setLoading(true);
        try {
            const res = yield AdditionalLeaderboardApi.getEntries(y);
            setEntries(res.data);
        }
        catch (_a) {
            setEntries([]);
        }
        finally {
            setLoading(false);
        }
    });
    useEffect(() => {
        if (!open)
            return;
        fetchEntries(year);
        AdditionalLeaderboardApi.getAgents()
            .then((res) => setAgents(res.data))
            .catch(() => setAgents([]));
    }, [open, year]);
    const filteredEntries = useMemo(() => {
        if (!searchText.trim())
            return entries;
        const lower = searchText.toLowerCase();
        return entries.filter((e) => e.salesAgentName.toLowerCase().includes(lower) ||
            e.entryDate.toLowerCase().includes(lower));
    }, [entries, searchText]);
    const handleOpenAdd = () => {
        setEditingId(null);
        setFormData(Object.assign({}, EMPTY_FORM));
        setSelectedAgent(null);
        setFormOpen(true);
    };
    const handleOpenEdit = (entry) => {
        var _a;
        setEditingId(entry.id);
        setFormData({
            salesAgentId: entry.salesAgentId,
            salesAgentName: entry.salesAgentName,
            entryDate: entry.entryDate.slice(0, 10),
            tiUitbreidingInOfferte: entry.tiUitbreidingInOfferte,
            tiUitbreidingGewonnen: entry.tiUitbreidingGewonnen,
            tiUitbreidingOmzet: entry.tiUitbreidingOmzet,
            tiMeerwerkOffertesGewonnen: entry.tiMeerwerkOffertesGewonnen,
            tiMeerwerkInkomsten: entry.tiMeerwerkInkomsten,
            ppeOffertesGewonnen: entry.ppeOffertesGewonnen,
            ppeInkomsten: entry.ppeInkomsten,
        });
        setSelectedAgent((_a = agents.find((a) => a.id === entry.salesAgentId)) !== null && _a !== void 0 ? _a : {
            id: entry.salesAgentId,
            name: entry.salesAgentName,
        });
        setFormOpen(true);
    };
    const handleSave = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!formData.salesAgentId || !formData.entryDate)
            return;
        setFormSaving(true);
        try {
            const payload = {
                salesAgentId: formData.salesAgentId,
                salesAgentName: formData.salesAgentName,
                entryDate: formData.entryDate,
                tiUitbreidingInOfferte: Number(formData.tiUitbreidingInOfferte),
                tiUitbreidingGewonnen: Number(formData.tiUitbreidingGewonnen),
                tiUitbreidingOmzet: Number(formData.tiUitbreidingOmzet),
                tiMeerwerkOffertesGewonnen: Number(formData.tiMeerwerkOffertesGewonnen),
                tiMeerwerkInkomsten: Number(formData.tiMeerwerkInkomsten),
                ppeOffertesGewonnen: Number(formData.ppeOffertesGewonnen),
                ppeInkomsten: Number(formData.ppeInkomsten),
            };
            if (editingId) {
                yield AdditionalLeaderboardApi.updateEntry(editingId, payload);
            }
            else {
                yield AdditionalLeaderboardApi.createEntry(payload);
            }
            setFormOpen(false);
            fetchEntries(year);
        }
        catch (_a) {
            // keep form open on error
        }
        finally {
            setFormSaving(false);
        }
    });
    const handleDeleteClick = (id) => {
        setDeletingId(id);
        setDeleteConfirmOpen(true);
    };
    const handleDeleteConfirm = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!deletingId)
            return;
        try {
            yield AdditionalLeaderboardApi.deleteEntry(deletingId);
            setDeleteConfirmOpen(false);
            setDeletingId(null);
            fetchEntries(year);
        }
        catch (_a) {
            setDeleteConfirmOpen(false);
        }
    });
    const inkomstenEfficyTotaal = Number(formData.tiUitbreidingOmzet) +
        Number(formData.tiMeerwerkInkomsten) +
        Number(formData.ppeInkomsten);
    const columns = [
        {
            field: "entryDate",
            headerName: "Datum",
            width: 110,
            valueFormatter: (value) => value ? new Date(value).toLocaleDateString("nl-NL") : "",
        },
        { field: "salesAgentName", headerName: "Verkoper", flex: 1, minWidth: 130 },
        {
            field: "tiUitbreidingInOfferte",
            headerName: "T&I Uitbr. Offerte",
            width: 100,
            type: "number",
            align: "left",
            headerAlign: "left",
        },
        {
            field: "tiUitbreidingGewonnen",
            headerName: "T&I Uitbr. Gewonnen",
            width: 100,
            type: "number",
            align: "left",
            headerAlign: "left",
        },
        {
            field: "tiUitbreidingOmzet",
            headerName: "T&I Uitbr. Omzet",
            width: 140,
            valueFormatter: (value) => convertToNLEuroFormat(value !== null && value !== void 0 ? value : 0),
        },
        {
            field: "tiMeerwerkOffertesGewonnen",
            headerName: "T&I Meerwerk Offertes",
            width: 100,
            type: "number",
            align: "left",
            headerAlign: "left",
        },
        {
            field: "tiMeerwerkInkomsten",
            headerName: "T&I Meerwerk Inkomsten",
            width: 150,
            valueFormatter: (value) => convertToNLEuroFormat(value !== null && value !== void 0 ? value : 0),
        },
        {
            field: "ppeOffertesGewonnen",
            headerName: "PPE Offertes",
            width: 100,
            type: "number",
            align: "left",
            headerAlign: "left",
        },
        {
            field: "ppeInkomsten",
            headerName: "PPE Inkomsten",
            width: 140,
            valueFormatter: (value) => convertToNLEuroFormat(value !== null && value !== void 0 ? value : 0),
        },
        {
            field: "actions",
            headerName: "",
            width: 90,
            sortable: false,
            disableColumnMenu: true,
            renderCell: (params) => (_jsxs(Box, { sx: { display: "flex", gap: 0.5, alignItems: "center", height: "100%" }, children: [canManage && (_jsx(IconButton, { size: "small", onClick: () => handleOpenEdit(params.row), sx: { color: "#6c757d", "&:hover": { color: "#130922" } }, children: _jsx(PencilSimple, { size: 16 }) })), canDelete && (_jsx(IconButton, { size: "small", onClick: () => handleDeleteClick(params.row.id), sx: { color: "#6c757d", "&:hover": { color: "#e03131" } }, children: _jsx(Trash, { size: 16 }) }))] })),
        },
    ];
    return (_jsxs(_Fragment, { children: [_jsxs(Dialog, { open: open, onClose: onClose, maxWidth: "lg", fullWidth: true, slotProps: {
                    paper: {
                        sx: {
                            borderRadius: 4,
                            height: "min(800px, calc(100vh - 64px))",
                            maxHeight: "calc(100vh - 64px)",
                            overflow: "hidden",
                            display: "flex",
                            flexDirection: "column",
                        },
                    }
                }, children: [_jsx(DialogTitle, { sx: { px: 3, pt: 2.5, pb: 2 }, children: _jsxs(Typography, { sx: { fontSize: 18, fontWeight: 600, color: "#130922" }, children: ["Uitkomsten invullen \u2014", " ", _jsx(Box, { component: "span", sx: { color: "#6c757d", fontWeight: 400 }, children: "Klassement Additioneel" })] }) }), _jsx(Divider, {}), _jsxs(DialogContent, { sx: {
                            display: "flex",
                            flexDirection: "column",
                            flex: 1,
                            overflow: "hidden",
                            px: 3,
                            pt: 2,
                            pb: 0,
                        }, children: [_jsxs(Box, { sx: {
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    flexWrap: "wrap",
                                    gap: 1.5,
                                    mb: 2,
                                }, children: [_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1.5 }, children: [_jsxs(Box, { sx: {
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 0.5,
                                                    border: "1px solid #e9ecef",
                                                    borderRadius: 2,
                                                    px: 1,
                                                    py: 0.375,
                                                    backgroundColor: "#fff",
                                                }, children: [_jsx(IconButton, { size: "small", onClick: () => setYear((y) => y - 1), sx: { p: 0.25, color: "#495057" }, children: _jsx(CaretLeft, { size: 15, weight: "bold" }) }), _jsx(Typography, { sx: {
                                                            fontWeight: 700,
                                                            minWidth: 38,
                                                            textAlign: "center",
                                                            fontSize: "0.9rem",
                                                            color: "#130922"
                                                        }, children: year }), _jsx(IconButton, { size: "small", onClick: () => setYear((y) => y + 1), disabled: year >= currentYear, sx: { p: 0.25, color: "#495057" }, children: _jsx(CaretRight, { size: 15, weight: "bold" }) })] }), _jsx(Chip, { label: `${entries.length} ${entries.length === 1 ? "invoer" : "invoeren"}`, size: "small", sx: {
                                                    backgroundColor: "#ede9fe",
                                                    color: "#130922",
                                                    fontWeight: 600,
                                                    fontSize: "0.7rem",
                                                    height: 22,
                                                } })] }), _jsxs(Box, { sx: { display: "flex", gap: 1.5, alignItems: "center" }, children: [_jsx(TextField, { size: "small", placeholder: "Zoek op naam...", value: searchText, onChange: (e) => setSearchText(e.target.value), sx: {
                                                    width: 260,
                                                    "& .MuiOutlinedInput-root": {
                                                        borderRadius: 2,
                                                        fontSize: "0.8125rem",
                                                        backgroundColor: "#fafafa",
                                                    },
                                                }, slotProps: {
                                                    input: {
                                                        endAdornment: (_jsx(InputAdornment, { position: "end", children: _jsx(MagnifyingGlass, { size: 16, color: "#adb5bd" }) })),
                                                    }
                                                } }), canCreate && (_jsx(Button, { variant: "contained", size: "small", startIcon: _jsx(Plus, { size: 14, weight: "bold" }), onClick: handleOpenAdd, sx: {
                                                    backgroundColor: "#130922",
                                                    "&:hover": { backgroundColor: "#2d1557" },
                                                    textTransform: "none",
                                                    borderRadius: 2,
                                                    px: 2,
                                                    py: 0.875,
                                                    fontWeight: 600,
                                                    fontSize: "0.8125rem",
                                                    whiteSpace: "nowrap",
                                                    boxShadow: "none",
                                                }, children: "Toevoegen" }))] })] }), _jsx(Box, { sx: { flex: 1, minHeight: 0 }, children: _jsx(DataGrid, { rows: filteredEntries, columns: columns, loading: loading, getRowId: (row) => row.id, pageSizeOptions: [10, 25, 50], initialState: { pagination: { paginationModel: { pageSize: 10 } } }, disableRowSelectionOnClick: true, sortingOrder: ["asc", "desc"], rowHeight: 52, localeText: { noRowsLabel: "Geen invoeren gevonden" }, sx: Object.assign(Object.assign({}, DATA_GRID_SX), { height: "100%", "& .MuiDataGrid-columnHeaders": { backgroundColor: "#f8f9fa" }, "& .MuiDataGrid-row:last-child .MuiDataGrid-cell": { borderBottom: "none" } }) }) })] }), _jsx(Divider, {}), _jsx(DialogActions, { sx: { px: 3, py: 1.75, flexShrink: 0 }, children: _jsx(Button, { onClick: onClose, variant: "outlined", size: "small", sx: {
                                borderColor: "#dee2e6",
                                color: "#495057",
                                textTransform: "none",
                                borderRadius: 2,
                                px: 3,
                                "&:hover": { borderColor: "#130922", color: "#130922", backgroundColor: "transparent" },
                            }, children: "Sluiten" }) })] }), _jsx(CustomModal, { open: formOpen, onClose: () => setFormOpen(false), size: "sm", saving: formSaving, handleSaveButtonClick: handleSave, heading: editingId ? "Uitkomst bewerken" : "Uitkomst toevoegen", saveButtonText: "Opslaan", disabled: formSaving || !formData.salesAgentId || !formData.entryDate, children: _jsx(Box, { sx: { px: 1, pt: "10px" }, children: _jsxs(Grid, { container: true, spacing: 2, children: [_jsx(Grid, { size: 12, children: _jsx(Autocomplete, { size: "small", options: agents, getOptionLabel: (o) => o.name, value: selectedAgent, onChange: (_, val) => {
                                        setSelectedAgent(val);
                                        setFormData((prev) => {
                                            var _a, _b;
                                            return (Object.assign(Object.assign({}, prev), { salesAgentId: (_a = val === null || val === void 0 ? void 0 : val.id) !== null && _a !== void 0 ? _a : "", salesAgentName: (_b = val === null || val === void 0 ? void 0 : val.name) !== null && _b !== void 0 ? _b : "" }));
                                        });
                                    }, renderInput: (params) => (_jsx(TextField, Object.assign({}, params, { label: "Verkoper *", size: "small" }))), isOptionEqualToValue: (opt, val) => opt.id === val.id }) }), _jsx(Grid, { size: 12, children: _jsx(TextField, { fullWidth: true, size: "small", label: "Datum", required: true, type: "date", value: formData.entryDate, onChange: (e) => setFormData((prev) => (Object.assign(Object.assign({}, prev), { entryDate: e.target.value }))), slotProps: {
                                        inputLabel: { shrink: true }
                                    } }) }), _jsx(Grid, { size: 12, children: _jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1.5, mt: 0.5 }, children: [_jsx(Typography, { sx: { fontSize: "0.6875rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#6c757d", whiteSpace: "nowrap" }, children: "T&I Uitbreiding" }), _jsx(Box, { sx: { flex: 1, height: "1px", backgroundColor: "#e9ecef" } })] }) }), _jsx(Grid, { size: 4, children: _jsx(TextField, { fullWidth: true, size: "small", label: "In Offerte", type: "number", value: formData.tiUitbreidingInOfferte, onChange: (e) => setFormData((prev) => (Object.assign(Object.assign({}, prev), { tiUitbreidingInOfferte: e.target.value }))), slotProps: {
                                        htmlInput: { min: 0, step: 1 }
                                    } }) }), _jsx(Grid, { size: 4, children: _jsx(TextField, { fullWidth: true, size: "small", label: "Gewonnen", type: "number", value: formData.tiUitbreidingGewonnen, onChange: (e) => setFormData((prev) => (Object.assign(Object.assign({}, prev), { tiUitbreidingGewonnen: e.target.value }))), slotProps: {
                                        htmlInput: { min: 0, step: 1 }
                                    } }) }), _jsx(Grid, { size: 4, children: _jsx(TextField, { fullWidth: true, size: "small", label: "Omzet", type: "number", value: formData.tiUitbreidingOmzet, onChange: (e) => setFormData((prev) => (Object.assign(Object.assign({}, prev), { tiUitbreidingOmzet: e.target.value }))), slotProps: {
                                        input: {
                                            startAdornment: (_jsx(InputAdornment, { position: "start", children: _jsx(Typography, { sx: {
                                                        fontSize: "0.875rem",
                                                        color: "text.secondary"
                                                    }, children: "\u20AC" }) })),
                                        },
                                        htmlInput: { min: 0, step: 0.01 }
                                    } }) }), _jsx(Grid, { size: 12, children: _jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1.5, mt: 0.5 }, children: [_jsx(Typography, { sx: {
                                                fontSize: "0.6875rem",
                                                fontWeight: 700,
                                                textTransform: "uppercase",
                                                letterSpacing: "0.08em",
                                                color: "#6c757d",
                                                whiteSpace: "nowrap",
                                            }, children: "T&I Meerwerk" }), _jsx(Box, { sx: { flex: 1, height: "1px", backgroundColor: "#e9ecef" } })] }) }), _jsx(Grid, { size: 6, children: _jsx(TextField, { fullWidth: true, size: "small", label: "Offertes gewonnen", type: "number", value: formData.tiMeerwerkOffertesGewonnen, onChange: (e) => setFormData((prev) => (Object.assign(Object.assign({}, prev), { tiMeerwerkOffertesGewonnen: e.target.value }))), slotProps: {
                                        htmlInput: { min: 0, step: 1 }
                                    } }) }), _jsx(Grid, { size: 6, children: _jsx(TextField, { fullWidth: true, size: "small", label: "Inkomsten", type: "number", value: formData.tiMeerwerkInkomsten, onChange: (e) => setFormData((prev) => (Object.assign(Object.assign({}, prev), { tiMeerwerkInkomsten: e.target.value }))), slotProps: {
                                        input: {
                                            startAdornment: (_jsx(InputAdornment, { position: "start", children: _jsx(Typography, { sx: {
                                                        fontSize: "0.875rem",
                                                        color: "text.secondary"
                                                    }, children: "\u20AC" }) })),
                                        },
                                        htmlInput: { min: 0, step: 0.01 }
                                    } }) }), _jsx(Grid, { size: 12, children: _jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1.5, mt: 0.5 }, children: [_jsx(Typography, { sx: {
                                                fontSize: "0.6875rem",
                                                fontWeight: 700,
                                                textTransform: "uppercase",
                                                letterSpacing: "0.08em",
                                                color: "#6c757d",
                                                whiteSpace: "nowrap",
                                            }, children: "PPE" }), _jsx(Box, { sx: { flex: 1, height: "1px", backgroundColor: "#e9ecef" } })] }) }), _jsx(Grid, { size: 6, children: _jsx(TextField, { fullWidth: true, size: "small", label: "Offertes gewonnen", type: "number", value: formData.ppeOffertesGewonnen, onChange: (e) => setFormData((prev) => (Object.assign(Object.assign({}, prev), { ppeOffertesGewonnen: e.target.value }))), slotProps: {
                                        htmlInput: { min: 0, step: 1 }
                                    } }) }), _jsx(Grid, { size: 6, children: _jsx(TextField, { fullWidth: true, size: "small", label: "Inkomsten", type: "number", value: formData.ppeInkomsten, onChange: (e) => setFormData((prev) => (Object.assign(Object.assign({}, prev), { ppeInkomsten: e.target.value }))), slotProps: {
                                        input: {
                                            startAdornment: (_jsx(InputAdornment, { position: "start", children: _jsx(Typography, { sx: {
                                                        fontSize: "0.875rem",
                                                        color: "text.secondary"
                                                    }, children: "\u20AC" }) })),
                                        },
                                        htmlInput: { min: 0, step: 0.01 }
                                    } }) }), _jsx(Grid, { size: 12, children: _jsxs(Box, { sx: {
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        backgroundColor: "#f0faf3",
                                        border: "1px solid #c3e6cb",
                                        borderRadius: 2,
                                        px: 2,
                                        py: 1.25,
                                        marginBottom: "10px"
                                    }, children: [_jsx(Typography, { sx: {
                                                fontSize: "0.875rem",
                                                color: "#495057",
                                                fontWeight: 500
                                            }, children: "Inkomsten totaal (berekend)" }), _jsx(Typography, { sx: {
                                                fontWeight: 700,
                                                color: "#2aa241",
                                                fontSize: "1rem"
                                            }, children: convertToNLEuroFormat(inkomstenEfficyTotaal) })] }) })] }) }) }), _jsxs(Dialog, { open: deleteConfirmOpen, onClose: () => setDeleteConfirmOpen(false), maxWidth: "xs", fullWidth: true, slotProps: {
                    paper: { sx: { borderRadius: 4, overflow: "hidden" } }
                }, children: [_jsx(Box, { sx: { height: 4, backgroundColor: "#e03131", flexShrink: 0 } }), _jsxs(DialogContent, { sx: { textAlign: "center", pt: 4.5, pb: 2, px: 4 }, children: [_jsx(Box, { sx: {
                                    width: 68,
                                    height: 68,
                                    borderRadius: "50%",
                                    backgroundColor: "#fff5f5",
                                    border: "2px solid #ffc9c9",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    margin: "0 auto 20px",
                                }, children: _jsx(Warning, { size: 34, color: "#e03131", weight: "fill" }) }), _jsx(Typography, { sx: {
                                    fontWeight: 700,
                                    fontSize: "1.125rem",
                                    color: "#130922",
                                    mb: 1.25
                                }, children: "Uitkomst verwijderen" }), _jsxs(Typography, { sx: {
                                    color: "#6c757d",
                                    fontSize: "0.875rem",
                                    lineHeight: 1.65
                                }, children: ["Weet je zeker dat je deze uitkomst wilt verwijderen?", " ", _jsx(Box, { component: "span", sx: { color: "#495057", fontWeight: 500 }, children: "Deze actie kan niet ongedaan worden gemaakt." })] })] }), _jsxs(DialogActions, { sx: { px: 4, pb: 4, pt: 2, gap: 1.5 }, children: [_jsx(Button, { onClick: () => setDeleteConfirmOpen(false), variant: "outlined", size: "medium", fullWidth: true, sx: {
                                    borderColor: "#dee2e6",
                                    color: "#495057",
                                    textTransform: "none",
                                    borderRadius: 2.5,
                                    fontWeight: 500,
                                    "&:hover": { borderColor: "#adb5bd", backgroundColor: "#f8f9fa" },
                                }, children: "Annuleren" }), _jsx(Button, { onClick: handleDeleteConfirm, variant: "contained", size: "medium", fullWidth: true, sx: {
                                    backgroundColor: "#e03131",
                                    "&:hover": { backgroundColor: "#c92a2a" },
                                    textTransform: "none",
                                    borderRadius: 2.5,
                                    fontWeight: 600,
                                    boxShadow: "0 2px 10px rgba(224,49,49,0.28)",
                                }, children: "Verwijderen" })] })] })] }));
};
